import { compareTo } from "@/lib/std.ts"

import type { SyntaxMarkupTheme } from "@/components/Diff/diffColors.ts"
import type { Property } from "csstype"


type MarkerTypeParams = {
  readonly name: string,
  readonly priority: number,
  readonly syntax: boolean,
  readonly styles?: (theme: SyntaxMarkupTheme) => MarkerStyle,
}

export class MarkerType {
  readonly name: string
  readonly priority: number
  readonly syntax: boolean
  readonly styles?: (theme: SyntaxMarkupTheme) => MarkerStyle

  constructor(params: MarkerTypeParams) {
    this.name = params.name
    this.priority = params.priority
    this.syntax = params.syntax
    this.styles = params.styles
  }

  compareTo(other: MarkerType): number {
    return -compareTo(this.priority, other.priority)
  }

  static CURRENT = new MarkerType({
    name: "Current",
    priority: 0,
    syntax: false,
    styles: theme => markerStyle({
      color: theme.CURRENT_TEXT,
      backgroundColor: theme.CURRENT_BACKGROUND,
      display: "inline-block",
    }),
  })

  static HIGHLIGHT = new MarkerType({
    name: "Highlight",
    priority: 1,
    syntax: false,
    styles: theme => ({
      color: theme.HIGHLIGHT_TEXT,
      backgroundColor: theme.HIGHLIGHT_BACKGROUND,
      display: "inline-block",
    }),
  })

  static DIFF_ADDED = new MarkerType({
    name: "Diff added",
    priority: 2,
    syntax: false,
    styles: theme => ({
      backgroundColor: theme.DIFF_ADDED,
      display: "inline-block",
    }),
  })

  static DIFF_DELETED = new MarkerType({
    name: "Diff deleted",
    priority: 3,
    syntax: false,
    styles: theme => ({
      backgroundColor: theme.DIFF_DELETED,
      display: "inline-block",
    }),
  })

  static CODE_FOLD = new MarkerType({
    name: "Folded",
    priority: 4,
    syntax: false,
  })

  static COMMENT = new MarkerType({
    name: "Comment",
    priority: 10,
    syntax: true,
    styles: theme => ({
      color: theme.COMMENT,
      fontStyle: "italic",
    }),
  })

  static NUM_LITERAL = new MarkerType({
    name: "Numeric literal",
    priority: 11,
    syntax: true,
    styles: theme => ({
      color: theme.NUM_LITERAL,
      fontWeight: "bolder",
    }),
  })

  static STRING_LITERAL = new MarkerType({
    name: "String literal",
    priority: 12,
    syntax: true,
    styles: theme => ({
      color: theme.STRING_LITERAL,
      fontWeight: "bolder",
    }),
  })

  static KEYWORD = new MarkerType({
    name: "Keyword",
    priority: 13,
    syntax: true,
    styles: theme => ({
      color: theme.KEYWORD,
      fontWeight: "bold",
    }),
  })

  static DECLARATION = new MarkerType({
    name: "Declaration",
    priority: 14,
    syntax: true,
    styles: theme => ({
      color: theme.DECLARATION,
      fontWeight: "bold",
    }),
  })

  static PARAMETER_ASSIGNMENT = new MarkerType({
    name: "Parameter assignment",
    priority: 15,
    syntax: true,
    styles: theme => ({
      color: theme.PARAMETER_ASSIGNMENT,
    }),
  })

  static IDENTIFIER = new MarkerType({
    name: "Identifier",
    priority: 16,
    syntax: true,
    styles: theme => ({
      color: theme.IDENTIFIER,
    }),
  })

  static IDENTIFIER_DECLARATION = new MarkerType({
    name: "Identifier declaration",
    priority: 18,
    syntax: true,
    styles: theme => ({
      color: theme.IDENTIFIER_DECLARATION,
    }),
  })

  static KEY = new MarkerType({
    name: "Key",
    priority: 19,
    syntax: true,
    styles: theme => ({
      color: theme.KEY,
      fontWeight: "bolder",
    }),
  })

  static TAG = new MarkerType({
    name: "Tag",
    priority: 20,
    syntax: true,
    styles: theme => ({
      color: theme.TAG,
      fontWeight: "bold",
    }),
  })

  static ANNOTATION = new MarkerType({
    name: "Annotation",
    priority: 21,
    syntax: true,
    styles: theme => ({
      color: theme.ANNOTATION,
    }),
  })

  static INLINE_ANNOTATION = new MarkerType({
    name: "Inline annotation",
    priority: 22,
    syntax: true,
    styles: theme => ({
      color: theme.INLINE_ANNOTATION,
    }),
  })

  static PREPROCESSOR_ANNOTATION = new MarkerType({
    name: "Preprocessor annotation",
    priority: 23,
    syntax: true,
    styles: theme => ({
      color: theme.PREPROCESSOR_ANNOTATION,
      fontWeight: "bold",
    }),
  })

  static PREPROCESSOR_MESSAGE = new MarkerType({
    name: "Preprocessor message",
    priority: 24,
    syntax: true,
    styles: theme => ({
      color: theme.PREPROCESSOR_MESSAGE,
    }),
  })

  static IDENTIFIER_TYPE = new MarkerType({
    name: "Identifier type",
    priority: 25,
    syntax: true,
    styles: theme => ({
      color: theme.IDENTIFIER_TYPE,
    }),
  })

  static INVOCATION = new MarkerType({
    name: "Invocation",
    priority: 26,
    syntax: true,
    styles: theme => ({
      color: theme.INVOCATION,
    }),
  })

  static DSL = new MarkerType({
    name: "DSL",
    priority: 27,
    syntax: true,
    styles: theme => ({
      color: theme.INVOCATION,
      fontStyle: "italic",
    }),
  })

  static CODE_ISSUE = new MarkerType({
    name: "Code analysis issue",
    priority: 40,
    syntax: false,
  })

  static NAVIGATION_SYMBOL = new MarkerType({
    name: "Navigation symbol",
    priority: 50,
    syntax: false,
    styles: () => ({
      color: "inherit",
    }),
  })
}

export type MarkerStyle = {
  readonly display?: Property.Display
  readonly color?: Property.Color
  readonly backgroundColor?: Property.BackgroundColor
  readonly fontStyle?: Property.FontStyle
  readonly fontWeight?: Property.FontWeight
}

function markerStyle(style: MarkerStyle): MarkerStyle {
  return style
}

export function stringifyMarkerStyle(style: MarkerStyle): string {
  const styles = [
    ["display", style.display],
    ["color", style.color],
    ["background-color", style.backgroundColor],
    ["font-style", style.fontStyle],
    ["font-weight", style.fontWeight]
  ]

  return styles.filter(it => Boolean(it[1])).map(it => `${it[0]}: ${it[1]}`).join("; ")
}